import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ChevronsUpDown,
  Fingerprint as FingerprintIcon,
  KeyRound,
  LogOutIcon,
  Settings,
  Users,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { P, usePermissions } from '@/features/permissions';
import { ChangePasswordDialog, PasskeysDialog } from '@/features/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Top-right account menu for the personal-edition shell, where the sidebar
 * (and its NavUser) is hidden. Built directly on shadcn DropdownMenu so it
 * has no dependency on the sidebar context.
 */
export function PersonalUserMenu() {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passkeysOpen, setPasskeysOpen] = useState(false);
  const { user, logout, organization } = useAuth();
  const navigate = useNavigate();
  const { can } = usePermissions();

  if (!user) return null;

  const initials = getInitials(user.name);
  const canManageUsers = can(P.MANAGE_USERS);

  return (
    <>
  <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" className="h-9 gap-2 px-2">
          <Avatar className="h-7 w-7">
            <AvatarImage src="" alt={user.name} />
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-[12ch] truncate text-sm font-medium sm:inline">{user.name}</span>
          <ChevronsUpDown aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={4} className="min-w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <span className="truncate text-sm font-medium">{user.name}</span>
            <span className="truncate text-xs text-muted-foreground">{user.email}</span>
            {organization?.organization_name && (
              <span className="mt-1 truncate text-xs text-muted-foreground">{organization.organization_name}</span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {canManageUsers && (
          <DropdownMenuItem onClick={() => navigate({ to: '/users' })}>
            <Users className="mr-2 h-4 w-4" />
            Shared access
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate({ to: '/settings' })}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
            <KeyRound className="mr-2 h-4 w-4" />
            Change password
          </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setPasskeysOpen(true)}>
          <FingerprintIcon />
          Passkeys
        </DropdownMenuItem>
          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={logout}>
          <LogOutIcon className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
      </DropdownMenu>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <PasskeysDialog open={passkeysOpen} onOpenChange={setPasskeysOpen} />
    </>
  );
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
